import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchEndpoints, fetchEndpointAlerts, isolateEndpoint, updateEndpointFindingStatus,
  fetchCommands, approveCommand, rejectCommand, recomputeEndpointRisk,
  registerEndpoint, submitRansomwareTelemetry,
} from '../../services/api/endpointService';

export const loadEndpoints = createAsyncThunk('endpoint/load', async () => {
  // Do not let one auxiliary endpoint turn the entire Endpoint Protection page blank.
  // Surface partial data and the failed sections so operators can still work.
  const [devicesResult, alertsResult, commandsResult] = await Promise.allSettled([
    fetchEndpoints(), fetchEndpointAlerts(), fetchCommands('PENDING_APPROVAL')
  ]);
  const failures = [devicesResult, alertsResult, commandsResult]
    .filter((r) => r.status === 'rejected')
    .map((r) => r.reason?.response?.data?.message || r.reason?.message || 'Request failed');
  return {
    devices: devicesResult.status === 'fulfilled' ? devicesResult.value : [],
    alerts: alertsResult.status === 'fulfilled' ? alertsResult.value : [],
    commands: commandsResult.status === 'fulfilled' ? commandsResult.value : [],
    partialError: failures.length ? failures.join(' | ') : null,
  };
});

export const registerDevice = createAsyncThunk('endpoint/register', async (payload) => {
  await registerEndpoint(payload);
  return fetchEndpoints();
});

// Sends a high-signal ransomware telemetry window for one device so the detection,
// finding and isolation-recommendation chain can be exercised end to end.
export const simulateRansomware = createAsyncThunk('endpoint/simulateRansomware', async (endpointId) => {
  const result = await submitRansomwareTelemetry({
    endpoint_id: endpointId, window_seconds: 60,
    files_renamed_count: 1000, files_with_new_ransomware_ext: 500,
    high_entropy_writes_per_minute: 1000, shadow_copy_deletion_observed: true,
    created_filenames: ['README_RESTORE_FILES.txt'],
  });
  const [devices, alerts] = await Promise.all([fetchEndpoints(), fetchEndpointAlerts()]);
  return { devices, alerts, detections: Array.isArray(result?.detections) ? result.detections.length : 0 };
});

export const isolateDevice = createAsyncThunk('endpoint/isolate', async (id) => isolateEndpoint(id));

export const setFindingStatus = createAsyncThunk('endpoint/setFindingStatus', async ({ id, status }) => {
  await updateEndpointFindingStatus(id, status);
  return fetchEndpointAlerts();
});

export const approvePendingCommand = createAsyncThunk('endpoint/approveCommand', async (id) => {
  await approveCommand(id);
  return fetchCommands('PENDING_APPROVAL');
});

export const rejectPendingCommand = createAsyncThunk('endpoint/rejectCommand', async ({ id, reason }) => {
  await rejectCommand(id, reason);
  return fetchCommands('PENDING_APPROVAL');
});

export const recomputeRisk = createAsyncThunk('endpoint/recomputeRisk', async (id) => {
  await recomputeEndpointRisk(id);
  return fetchEndpoints();
});

const endpointSlice = createSlice({
  name: 'endpoint',
  initialState: { status: 'idle', error: null, devices: [], alerts: [], commands: [] },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadEndpoints.pending, (state) => { state.status = 'loading'; })
      .addCase(loadEndpoints.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.devices = action.payload.devices;
        state.alerts = action.payload.alerts;
        state.commands = action.payload.commands;
        state.error = action.payload.partialError;
      })
      .addCase(loadEndpoints.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(isolateDevice.fulfilled, (state, action) => {
        const d = state.devices.find((dev) => dev.id === action.payload.id);
        if (d) {
          d.isolated = Boolean(action.payload.isolated);
          d.isolationPending = Boolean(action.payload.isolationPending);
        }
      })
      .addCase(registerDevice.fulfilled, (state, action) => { state.devices = action.payload; })
      .addCase(simulateRansomware.fulfilled, (state, action) => {
        state.devices = action.payload.devices;
        state.alerts = action.payload.alerts;
      })
      .addCase(setFindingStatus.fulfilled, (state, action) => { state.alerts = action.payload; })
      .addCase(approvePendingCommand.fulfilled, (state, action) => { state.commands = action.payload; })
      .addCase(rejectPendingCommand.fulfilled, (state, action) => { state.commands = action.payload; })
      .addCase(recomputeRisk.fulfilled, (state, action) => { state.devices = action.payload; });
  }
});

export default endpointSlice.reducer;
